<template>
  <footer class="chat-composer" :class="{ 'is-mobile': mobile }">
    <div v-if="showControls || $slots.tools" class="composer-tools">
      <slot name="tools" />
      <AgentServiceModeSwitch
        v-if="showControls"
        :policy="policy"
        :control-state="controlState"
        :changing="controlChanging"
        @mode-change="$emit('mode-change', $event)"
      />
      <AgentServiceTranslationSwitch
        v-if="showControls"
        :translation-mode="translationMode"
        @update:translation-mode="$emit('update:translation-mode', $event)"
      />
    </div>
    <!-- 聊天消息 -->
    <textarea
      ref="input"
      :value="value"
      :disabled="disabled || sending || controlChanging"
      :placeholder="resolvedPlaceholder"
      :aria-label="$t('agentServiceUI.messageInput')"
      rows="1"
      @input="onInput"
      @click="emitPosition"
      @keydown="onKeydown"
    ></textarea>
    <!-- 发送消息 -->
    <!-- 发送 -->
    <AgentServiceSendButton
      :value="value"
      :disabled="!canSubmit || disabled || sending || controlChanging"
      @send="send"
    />
  </footer>
</template>
<script>
import AgentServiceModeSwitch from "./AgentServiceModeSwitch.vue";
import AgentServiceTranslationSwitch from "./AgentServiceTranslationSwitch.vue";
import AgentServiceSendButton from "./AgentServiceSendButton.vue";

/** 两类客服共用输入视觉与键盘交互，真正发送仍交由各自业务容器处理。 */
export default {
  name: "AgentServiceComposer",
  components: { AgentServiceModeSwitch, AgentServiceTranslationSwitch, AgentServiceSendButton },
  props: {
    mobile: { type: Boolean, default: false },
    enterToSend: { type: Boolean, default: true },
    value: { type: String, default: "" },
    showControls: { type: Boolean, default: true },
    disabled: { type: Boolean, default: false },
    sending: { type: Boolean, default: false },
    canSubmit: { type: Boolean, default: false },
    placeholder: { type: String, default: "输入消息，按 Enter 发送" },
    policy: {
      type: Object,
      default: () => ({ participantType: "NONE" }),
    },
    controlState: { type: String, default: "" },
    controlChanging: { type: Boolean, default: false },
    translationMode: { type: String, default: "bilingual" },
  },
  computed: {
    resolvedPlaceholder() {
      return this.placeholder === "输入消息，按 Enter 发送"
        // 输入消息，按 Enter 发送
        ? this.$t(this.enterToSend === false ? "messageCenter.enterMessage" : "agentServiceUI.composerPlaceholder")
        : this.placeholder;
    },
  },
  watch: {
    value() { if (this.mobile) this.$nextTick(this.resizeMobileInput); },
  },
  methods: {
    onInput(event) {
      this.$emit("input", event.target.value);
      this.resizeMobileInput();
      this.emitPosition(event);
    },
    resizeMobileInput() {
      const input = this.$refs?.input;
      if (!this.mobile || !input) return;
      input.style.height = 'auto';
      input.style.height = Math.min(96, Math.max(44, input.scrollHeight)) + 'px';
    },
    emitPosition(event) {
      this.$emit("input-position", { value: event.target.value, caret: event.target.selectionStart, source: event.type });
    },
    onKeydown(event) {
      this.$emit("keydown", event);
      if (event.defaultPrevented || event.isComposing || event.keyCode === 229) return;
      if (this.enterToSend !== false && event.key === "Enter" && !event.shiftKey && !event.ctrlKey && !event.altKey && !event.metaKey) {
        event.preventDefault(); this.send();
      }
    },
    /** 原订单初始化仍可通过组件引用聚焦输入框。 */
    focus() {
      this.$refs.input?.focus();
    },
    /** 保持按钮和Enter同样的发送门槛，不重复触发已在途请求。 */
    send() {
      if (this.disabled || this.sending || this.controlChanging || !this.canSubmit) return;
      this.$emit("send");
    },
  },
};
</script>
<style lang="less" scoped>
.chat-composer {
  &.is-mobile {
    flex-wrap: wrap;
    gap: 8px;
    padding: 8px 12px calc(8px + env(safe-area-inset-bottom, 0px));
    .composer-tools { flex: 0 0 100%; min-width: 0; overflow-x: auto; }
    textarea { font-size: 16px; line-height: 24px; min-height: 44px; max-height: 96px; }
  }
  display: flex;
  flex: 0 0 auto;
  align-items: flex-end;
  gap: 8px;
  padding: 8px 12px calc(8px + env(safe-area-inset-bottom));
  border-top: 1px solid #eef2f7;
  background: #fff;
  .composer-tools {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 0;
  }
  textarea {
    flex: 1;
    min-width: 0;
    min-height: 44px;
    max-height: 100px;
    box-sizing: border-box;
    padding: 10px 12px;
    resize: none;
    border: 1px solid #dde3ee;
    border-radius: var(--chat-radius, 12px);
    outline: none;
    color: #334155;
    font: inherit;
    font-size: 14px;
    line-height: 22px;
    &:focus {
      border-color: #4286f5;
      box-shadow: 0 0 0 3px rgba(66, 134, 245, 0.12);
    }
    &:disabled {
      background: #f8fafc;
      color: #667085;
    }
  }
}
</style>
